import React, { useCallback, useContext, useState } from 'react';
import { Container, Link } from '@printdeal/ui-library';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { InView } from 'react-intersection-observer';

import { useSegmentation } from '@printdeal/segmentation/react';
import { useExperiment } from '@printdeal/feature-management/react';
import { Paths } from '../../../types/gatsby';
import Logo from './Logo';
import CustomerCareNavigation from './CustomerCareNavigation';
import ReOrderNavigation from '../../re-order/ReOrderNavigation';
import CartNavigation from './CartNavigation';
import AccountNavigation from './account-navigation';
import MainNavigation from './main-navigation';
import Search from './search';
import { dispatchDataLayerStickyHeaderView } from './helpers';
import LanguageSwitch from './language-switch';
import HeaderContextProvider from './HeaderContextProvider';
import { HeaderContext } from './context';
import ResellerCustomerCareNavigation from '../../customer-programs/CustomerServiceNavigation/ResellerCustomerCareNavigation';

/**
 * Notes:
 * - Markup is not mobile-first to provide correct tab-index order on desktop.
 */

type HeaderProps = {
  localizedPaths?: Paths;
  isSearchSmall?: boolean;
  isStickyDisabled?: boolean;
};

const Header = ({
  localizedPaths,
  isSearchSmall,
  isStickyDisabled,
}: HeaderProps) => {
  const {
    isSticky,
    setIsSticky,
    isSearchOpen,
    isClearSearch,
    setIsClearSearch,
  } = useContext(HeaderContext);
  const [isStickyEventDispatched, setIsStickyEventDispatched] = useState(false);
  const { data: segmentationData } = useSegmentation();
  const { isReseller } = segmentationData ?? { isReseller: false };
  const { experiment, isInitialized } = useExperiment('resailors_exp1876_hide_reorder_link');
  const isExp1876Active = isInitialized && experiment.variation === 'variant';

  const handleHeaderInView = useCallback((inView: boolean) => {
    if (isStickyDisabled) return;

    setIsSticky(!inView);

    // Only dispatch this event once.
    if (!isStickyEventDispatched && !inView) {
      dispatchDataLayerStickyHeaderView();
      setIsStickyEventDispatched(true);
    }
  }, [isStickyDisabled, isStickyEventDispatched]);

  return (
    <>
      <header
        id="pd-header"
        className={clsx(
          'pd-z-30',
          isSticky ? 'pd-fixed pd-inset-x-0 pd-top-0' : 'pd-relative',
        )}
      >
        <div className="pd-absolute pd-inset-0 -pd-z-10 pd-border-b pd-bg-white" />
        <LanguageSwitch variant="desktop" localizedPaths={localizedPaths} className="pd-hidden lg:pd-block" />
        <div
          className={clsx(
            'pd-border-b pd-py-5',
            !isSticky && 'md:pd-border-b-0',
            isSticky && !isSearchOpen && 'lg:pd-pt-0',
            !isSearchOpen && 'lg:pd-pb-0',
          )}
        >
          <Container className="pd-flex pd-flex-wrap pd-items-center pd-gap-x-4 lg:pd-gap-x-5">
            <Logo
              className={clsx(
                'pd-order-1',
                isSearchOpen && 'pd-hidden lg:pd-block',
                isSticky && !isSearchOpen && 'lg:pd-hidden',
              )}
            />
            {/* We're linking the label with the "header-search" ID which is passed to the underlying <input> */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="header-search" className="pd-sr-only">
              <FormattedMessage id="search.bar.label" defaultMessage="Search website" />
            </label>
            <Search
              id="header-search"
              className={clsx(
                'pd-order-2 pd-ml-auto pd-grow max-lg:pd-order-7',
                isSearchSmall && !isExp1876Active && 'lg:pd-mr-auto lg:pd-max-w-xl',
                // Inputs don't shrink as far as we need to, so add max-width to force it.
                isSearchOpen ? 'pd-max-w-[calc(100%-6.75rem)]' : 'pd-max-w-[calc(100%-6rem)]',
              )}
              // Search still implements the reversed sticky API.
              staticHeaderInView={!isSticky}
            />
            {!isExp1876Active && <ReOrderNavigation className={clsx('pd-order-3', isSearchOpen && 'pd-hidden')} />}
            <AccountNavigation className={clsx('pd-order-4', isSearchOpen && 'pd-hidden')} />
            <CartNavigation className={clsx('pd-order-5', isSearchOpen && 'pd-hidden')} />
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              as="button"
              type="button"
              variant="secondary"
              onClick={() => setIsClearSearch(!isClearSearch)}
              iconBefore="times"
              id="search-close"
              className={clsx('pd-order-3 max-lg:pd-order-1', !isSearchOpen && 'pd-hidden')}
            >
              <FormattedMessage id="search.results.close" defaultMessage="Close" />
            </Link>
            <hr
              className={clsx(
                'pd-order-5 pd-mb-4 pd-h-0 pd-basis-full pd-border-0 lg:pd-mb-5',
                isSearchOpen && 'pd-hidden',
                isSticky && 'lg:pd-hidden',
              )}
            />
            <MainNavigation
              localizedPaths={localizedPaths}
              className={clsx(
                'pd-order-6',
                isSearchOpen && 'pd-hidden',
                isSticky && 'lg:pd-order-1',
              )}
            />
            <div
              className={clsx(
                'pd-order-7 pd-ml-auto pd-flex pd-items-center pd-gap-5 max-lg:pd-order-2',
                isSearchOpen && 'pd-hidden',
                isSticky && 'lg:pd-hidden',
              )}
            >
              {
                isReseller
                  ? <ResellerCustomerCareNavigation /> : <CustomerCareNavigation />
              }
            </div>
          </Container>
        </div>
      </header>
      <InView delay={50} onChange={handleHeaderInView}>
        {({ ref }) => (
          <div
            ref={ref}
            // This div replaces the static header height when header is sticky.
            className={clsx(isSticky && 'pd-h-[8.0625rem] sm:pd-h-[8.5rem] lg:pd-h-[9rem]')}
          />
        )}
      </InView>
    </>
  );
};

const HeaderWithProviders = (props: HeaderProps) => (
  <HeaderContextProvider>
    <Header {...props} />
  </HeaderContextProvider>
);

export default HeaderWithProviders;
